import { join } from './path'

interface TreeNodeBase {
  id: string
  parentId: string | null
  name: string
}

interface PathedTreeNode extends TreeNodeBase {
  path: string
}

type TreeNode<T> = T & {
  parent: TreeNode<T> | null
  children: TreeNode<T>[]
}

const walkTreeDownDepthFirst = <T extends TreeNodeBase>(node: TreeNode<T>, walker: (node: TreeNode<T>) => 'abort' | undefined | void): boolean => {
  if (walker(node) === 'abort') {
    return false
  }

  for (const child of node.children) {
    if (!walkTreeDownDepthFirst(child, walker)) {
      return false
    }
  }
  return true
}

/**
 * Transforms the list of nodes to possibly multiple trees. Returns the root elements of these trees in a list.
 * Trees that contain loops will not be returned as roots and will instead be skipped.
 */
export const buildTrees = <T extends TreeNodeBase>(list: T[]): TreeNode<T>[] => {
  const nodeDictionary: Record<string, TreeNode<T>> = {}
  for (const element of list) {
    const id = `${element.id}`
    if (id in nodeDictionary) {
      throw new Error(`Found multiple elements with the same id: ${id}`)
    }
    nodeDictionary[id] = {
      ...element,
      parent: null,
      children: [],
    }
  }

  for (const node of Object.values(nodeDictionary)) {
    if (node.parentId !== null) {
      const parent = nodeDictionary[node.parentId]
      if (parent !== undefined) {
        node.parent = parent
        parent.children.push(node)
      }
    }
  }

  return Object.values(nodeDictionary).filter((node) => node.parent === null)
}

/**
 * Modifies the given tree structure and adds a path property combined of the path of the parent and the nodes name, separated by /.
 * The root nodes start their path at the value given by basePath.
 */
export const generatePaths = <T extends TreeNodeBase>(trees: TreeNode<T>[], basePath?: string): TreeNode<T & PathedTreeNode>[] => {
  const changedTree = trees as unknown as TreeNode<T & PathedTreeNode>[]
  for (const root of changedTree) {
    if (basePath !== undefined) {
      root.path = join(basePath, root.name)
    } else {
      root.path = root.name
    }
    
    for (const child of root.children) {
      walkTreeDownDepthFirst(child, (node) => {
        node.path = join(node.parent!.path, node.name)
      })
    }
  }
  return changedTree
}

/**
 * Returns the node of the given trees, whose path is the closest to the requested path.
 */
export const findClosestNode = <T extends PathedTreeNode>(trees: TreeNode<T>[], path: string): TreeNode<T> | null => {
  const matches: TreeNode<T>[] = []
  for (const root of trees) {
    if (path === root.path || path.startsWith(`${root.path}/`)) {
      matches.push(root)
      const closest = findClosestNode(root.children, path)
      if (closest !== null) {
        matches.push(closest)
      }
    }
  }

  if (matches.length === 0) {
    return null
  }

  return matches.reduce((longestMatch, match) => {
    return match.path.length > longestMatch.path.length ? match : longestMatch
  }, matches[0])
}

export const findNodeAlongParents = <T extends TreeNodeBase>(node: TreeNode<T>, nodeName: string): T | null => {
  const candidate = node.children.find((child) => child.name === nodeName)
  if (candidate !== undefined) {
    return candidate
  }

  if (node.parent !== null) {
    return findNodeAlongParents(node.parent, nodeName)
  }

  return null
}

/**
 * Returns the path of the given subject in the tree, or null if it is not found.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any seems appropiate here, we don't know any more specific type
export function pathInTree(tree: object, predicate: (element: any) => boolean): string | null {
  if (predicate(tree)) {
    return ''
  }

  for (const [key, value] of Object.entries(tree)) {
    if (predicate(value)) {
      return key
    } else if (typeof value === 'object' && value !== null) {
      const found = pathInTree(value, predicate)
      if (found !== null) {
        return join(key, found)
      }
    }
  }

  return null
}
